import * as React from "react"
import { formatDistanceToNow } from "date-fns"
import maplibregl from "maplibre-gl"
import { createRoot, Root } from "react-dom/client"

import "maplibre-gl/dist/maplibre-gl.css"

import type { MergedDeviceType } from "@/types"
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu"

import { env } from "@/env"
import type { ConsumerConnectionBillingStatusType } from "@/types/billing"
import { Icons } from "@/components/icons"
import { KMZLayerLibre } from "@/components/map/map-libre/kmz-layer-libre"
import { MapStyleSwitcher } from "@/components/map/map-libre/map-style-switcher"
import { MapLibreContext } from "@/components/map/map-libre/maplibre-context"

import { Card, CardTitle } from "../ui/card"
import { Separator } from "../ui/separator"
import { Sheet, SheetContent } from "../ui/sheet"

// ---------- Custom Marker Components ----------
const ConsumerMarker: React.FC<{
  consumer: ConsumerConnectionBillingStatusType
  onClick: () => void
}> = ({ consumer, onClick }) => (
  <div
    className="consumer-marker cursor-pointer"
    onClick={onClick}
    style={{
      width: 32,
      height: 32,
      background: "#3b82f6",
      borderRadius: "50%",
      border: "2px solid white",
      boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
    }}
  />
)

const TrackerMarker: React.FC<{
  device: MergedDeviceType
  onClick: () => void
}> = ({ device, onClick }) => (
  <div className="tracker-marker relative cursor-pointer" onClick={onClick}>
    {/* Person icon without background circle */}
    <Icons.dogWaving className="h-12 w-12 text-green-500 drop-shadow-lg" />
    {/* Permanent tooltip with basic info */}
    <div className="absolute left-10 top-0 whitespace-nowrap rounded-lg border bg-background p-2 text-foreground shadow-lg">
      <div className="font-semibold">{device.full_name}</div>
      <div className="flex items-center text-sm">
        <Icons.creditCard className="mr-1 h-3 w-3" />
        {device.device_id}
      </div>
      <div className="flex items-center text-sm">
        <Icons.clock className="mr-1 h-3 w-3" />
        {formatDistanceToNow(new Date(device.timestamp), { addSuffix: true })}
      </div>
    </div>
  </div>
)

// ---------- Main Map Component ----------
interface TrackingMapLibreProps {
  position: [number, number]
  markers?: ConsumerConnectionBillingStatusType[]
  trackMarkers?: MergedDeviceType[]
  selectedLocation?: [number, number] | null
  kmzFiles?: Array<{ id: string | number; s3_url: string }>
  theme?: "light" | "dark"
}

export function TrackingMapLibre({
  position,
  markers,
  trackMarkers,
  selectedLocation,
  kmzFiles = [],
  theme = "light",
}: TrackingMapLibreProps) {
  const mapContainer = React.useRef<HTMLDivElement>(null)
  const map = React.useRef<maplibregl.Map | null>(null)
  const [mapLoaded, setMapLoaded] = React.useState(false)

  // Store marker instances
  const consumerMarkers = React.useRef<
    Map<
      string,
      {
        marker: maplibregl.Marker
        element: HTMLDivElement
        root: Root
        data: ConsumerConnectionBillingStatusType
      }
    >
  >(new Map())

  const trackerMarkersRef = React.useRef<
    Map<
      string,
      {
        marker: maplibregl.Marker
        element: HTMLDivElement
        root: Root
        data: MergedDeviceType
      }
    >
  >(new Map())

  // Selected items for sheets
  const [selectedConsumer, setSelectedConsumer] =
    React.useState<ConsumerConnectionBillingStatusType | null>(null)
  const [selectedTracker, setSelectedTracker] =
    React.useState<MergedDeviceType | null>(null)

  const lngLatCenter: [number, number] = [position[1], position[0]]

  // Initialize map
  React.useEffect(() => {
    if (!mapContainer.current) return

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://api.maptiler.com/maps/streets/style.json?key=${env.VITE_MAPTILER_KEY}`,
      center: lngLatCenter,
      zoom: 16,
      pitch: 70,
      bearing: 45,
      renderWorldCopies: true,
    })

    map.current.addControl(new maplibregl.NavigationControl(), "top-right")

    map.current.on("load", () => {
      setMapLoaded(true)
    })

    return () => {
      consumerMarkers.current.forEach(({ root, marker }) => {
        root.unmount()
        marker.remove()
      })
      trackerMarkersRef.current.forEach(({ root, marker }) => {
        root.unmount()
        marker.remove()
      })
      map.current?.remove()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update center when position changes
  React.useEffect(() => {
    if (map.current && mapLoaded && position) {
      map.current.flyTo({
        center: [position[1], position[0]],
        duration: 1000,
      })
    }
  }, [position, mapLoaded])

  // Fly to selected location
  React.useEffect(() => {
    if (map.current && mapLoaded && selectedLocation) {
      map.current.flyTo({
        center: [selectedLocation[1], selectedLocation[0]],
        duration: 1000,
        zoom: 18,
      })
    }
  }, [selectedLocation, mapLoaded])

  // Sync consumer markers
  React.useEffect(() => {
    if (!map.current || !mapLoaded) return

    const expectedConsumers = new Map(
      markers?.map((c) => [`consumer-${c.consumer_id}`, c]) ?? [],
    )

    expectedConsumers.forEach((consumer, id) => {
      const existing = consumerMarkers.current.get(id)
      if (existing) {
        existing.marker.setLngLat([consumer.lon, consumer.lat])
        existing.root.render(
          <ConsumerMarker
            consumer={consumer}
            onClick={() => setSelectedConsumer(consumer)}
          />,
        )
        existing.data = consumer
      } else {
        const el = document.createElement("div")
        el.className = "maplibre-consumer-marker"
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([consumer.lon, consumer.lat])
          .addTo(map.current!)

        const root = createRoot(el)
        root.render(
          <ConsumerMarker
            consumer={consumer}
            onClick={() => setSelectedConsumer(consumer)}
          />,
        )

        consumerMarkers.current.set(id, {
          marker,
          element: el,
          root,
          data: consumer,
        })
      }
    })

    consumerMarkers.current.forEach((inst, id) => {
      if (!expectedConsumers.has(id)) {
        inst.root.unmount()
        inst.marker.remove()
        consumerMarkers.current.delete(id)
      }
    })
  }, [markers, mapLoaded])

  // Sync tracker markers
  React.useEffect(() => {
    if (!map.current || !mapLoaded) return

    const expectedTrackers = new Map(
      trackMarkers?.map((d) => [`tracker-${d.device_id}`, d]) ?? [],
    )

    expectedTrackers.forEach((device, id) => {
      const existing = trackerMarkersRef.current.get(id)
      if (existing) {
        existing.marker.setLngLat([device.longitude, device.latitude])
        existing.root.render(
          <TrackerMarker
            device={device}
            onClick={() => setSelectedTracker(device)}
          />,
        )
        existing.data = device
      } else {
        const el = document.createElement("div")
        el.className = "maplibre-tracker-marker"
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([device.longitude, device.latitude])
          .addTo(map.current!)

        const root = createRoot(el)
        root.render(
          <TrackerMarker
            device={device}
            onClick={() => setSelectedTracker(device)}
          />,
        )

        trackerMarkersRef.current.set(id, {
          marker,
          element: el,
          root,
          data: device,
        })
      }
    })

    trackerMarkersRef.current.forEach((inst, id) => {
      if (!expectedTrackers.has(id)) {
        inst.root.unmount()
        inst.marker.remove()
        trackerMarkersRef.current.delete(id)
      }
    })
  }, [trackMarkers, mapLoaded])

  return (
    <div className="relative h-full w-full">
      <div ref={mapContainer} className="absolute inset-0" />

      {mapLoaded && (
        <div className="absolute left-4 top-4 z-10">
          <MapStyleSwitcher variant="dropdown" />
        </div>
      )}

      <MapLibreContext.Provider value={map.current}>
        <div className="absolute left-52 top-0 z-10">
          <MapStyleSwitcher variant="dropdown" />
        </div>
        {mapLoaded &&
          kmzFiles.map((kmz) => (
            <KMZLayerLibre key={kmz.id} kmzUrl={kmz.s3_url} kmz={kmz} />
          ))}
      </MapLibreContext.Provider>

      {/* Consumer Sheet */}
      {selectedConsumer && (
        <Sheet
          open={!!selectedConsumer}
          onOpenChange={(open) => !open && setSelectedConsumer(null)}
        >
          <SheetContent className="flex flex-col items-center justify-around p-0">
            <div className="mt-16 flex flex-col items-center p-8">
              <Icons.circleUser className="h-24 w-24" />
              <p className="mt-2 text-2xl font-semibold text-primary">
                {selectedConsumer.consumer_name}
              </p>
            </div>
            <Card className="h-screen w-full rounded-b-none rounded-t-[50px] p-8">
              <div>
                <CardTitle className="mb-4 mt-10 text-primary">
                  Meter Information
                </CardTitle>
                <DropdownMenuSeparator />
                <div className="mt-4 flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    <Icons.circleGauge className="mr-2 h-4 w-4" />
                    <p>160868650</p>
                  </span>
                </div>
                <CardTitle className="mb-4 mt-10 text-primary">
                  Status
                </CardTitle>
                <DropdownMenuSeparator />
                <div className="mt-4 flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    <Icons.user className="mr-2 h-4 w-4" />
                    {selectedConsumer.connection_status}
                  </span>
                  <span className="flex items-center gap-2">
                    <Icons.creditCard className="mr-2 h-4 w-4" />
                    {selectedConsumer.billing_status.replace("_", " ")}
                  </span>
                </div>
                <CardTitle className="mb-4 mt-10 text-primary">
                  Location Information
                </CardTitle>
                <DropdownMenuSeparator />
                <div className="mt-4 flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    <Icons.mapPin className="mr-2 h-4 w-4" />
                    {`Coordinates: ${selectedConsumer.lat} - ${selectedConsumer.lon}`}
                  </span>
                </div>
              </div>
            </Card>
          </SheetContent>
        </Sheet>
      )}

      {/* Tracker Sheet - expanded to match original UserDetailsSheet */}
      {selectedTracker && (
        <Sheet
          open={!!selectedTracker}
          onOpenChange={(open) => !open && setSelectedTracker(null)}
        >
          <SheetContent className="sm:max-w-md">
            <div className="mt-6 space-y-6">
              {/* Personal Information */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-primary">
                  Personal Information
                </h3>
                <Separator />
                <div className="grid grid-cols-[24px_1fr] items-center gap-x-2 gap-y-4">
                  <Icons.mail className="h-5 w-5 text-muted-foreground" />
                  <p>{selectedTracker.email ?? "N/A"}</p>
                  <Icons.phone className="h-5 w-5 text-muted-foreground" />
                  <p>{selectedTracker.mobile_number ?? "N/A"}</p>
                  <Icons.home className="h-5 w-5 text-muted-foreground" />
                  <p>{selectedTracker.address ?? "N/A"}</p>
                  <Icons.user className="h-5 w-5 text-muted-foreground" />
                  <p>{selectedTracker.gender ?? "N/A"}</p>
                </div>
              </div>

              {/* Device Information */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-primary">
                  Device Information
                </h3>
                <Separator />
                <p>
                  <span className="font-medium">Device ID:</span>{" "}
                  {selectedTracker.device_id ?? "Unknown"}
                </p>
              </div>

              {/* Location Information */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-primary">
                  Location Information
                </h3>
                <Separator />
                <div className="space-y-2 rounded-lg bg-muted p-4">
                  {selectedTracker.latitude != null &&
                  selectedTracker.longitude != null ? (
                    <div className="flex items-center space-x-2">
                      <Icons.mapPin className="h-5 w-5 text-muted-foreground" />
                      <p className="font-medium">
                        {selectedTracker.latitude.toFixed(6)},{" "}
                        {selectedTracker.longitude.toFixed(6)}
                      </p>
                    </div>
                  ) : (
                    <p>Location data not available</p>
                  )}
                  {selectedTracker.timestamp && (
                    <p>
                      <span className="font-medium">Last Updated:</span>{" "}
                      {new Date(selectedTracker.timestamp).toLocaleString()}
                    </p>
                  )}
                  {selectedTracker.accuracy != null && (
                    <p>
                      <span className="font-medium">Accuracy:</span>{" "}
                      {selectedTracker.accuracy.toFixed(2)} meters
                    </p>
                  )}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  )
}
